import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { FormDialog } from '@/components/shared/form-dialog';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { formatDateTime } from '@/utils/format';

interface PublicDevice {
  id: string;
  deviceName: string;
  platform: string;
  status: 'pending' | 'active' | 'revoked' | 'expired';
  enrolledAt: string | null;
  lastSeenAt: string | null;
  createdAt: string;
}

/** A short label people can recognise — not the raw user-agent string. */
const suggestBrowserDeviceName = (): string => {
  if (typeof navigator === 'undefined') return 'This browser';

  const ua = navigator.userAgent;
  let browser = 'Browser';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) browser = 'Safari';

  let place = 'computer';
  if (/iPhone|iPad/.test(ua)) place = 'iPhone';
  else if (/Android/.test(ua)) place = 'Android';
  else if (/Mac OS/.test(ua)) place = 'Mac';
  else if (/Windows/.test(ua)) place = 'Windows';
  else if (/Linux/.test(ua)) place = 'Linux';

  return `${browser} on ${place}`;
};

export const DevicesPage = () => {
  const { t } = useTranslation('security');
  const { t: tTips } = useTranslation('tips');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [enrolOpen, setEnrolOpen] = useState(false);
  const [deviceName, setDeviceName] = useState(() => suggestBrowserDeviceName());
  const [renameTarget, setRenameTarget] = useState<PublicDevice | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [revokeTarget, setRevokeTarget] = useState<PublicDevice | null>(null);
  const [revokeAllOpen, setRevokeAllOpen] = useState(false);
  const [removeRevokedOpen, setRemoveRevokedOpen] = useState(false);

  const devices = useQuery({
    queryKey: ['devices', 'mine'],
    queryFn: () => api.get<{ data: PublicDevice[]; page: unknown }>('/devices/mine'),
  });

  const enrol = useMutation({
    mutationFn: (name: string) =>
      api.post<PublicDevice>('/devices/enrol', {
        deviceName: name.trim(),
        platform: 'web',
        appVersion: 'console',
      }),
    onSuccess: () => {
      setEnrolOpen(false);
      toast.success(t('devices.enrolSuccess'));
      void queryClient.invalidateQueries({ queryKey: ['devices', 'mine'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.patch<PublicDevice>(`/devices/mine/${id}`, { deviceName: name.trim() }),
    onSuccess: () => {
      toast.success(t('devices.renamed'));
      setRenameTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['devices', 'mine'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const revoke = useMutation({
    mutationFn: (deviceId: string) => api.post<PublicDevice>(`/devices/mine/${deviceId}/revoke`, {}),
    onSuccess: () => {
      toast.success(t('devices.revoked'));
      setRevokeTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['devices', 'mine'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const revokeAll = useMutation({
    mutationFn: () => api.post<{ revokedCount: number }>('/devices/mine/revoke-all', {}),
    onSuccess: (result) => {
      toast.success(t('devices.revokedAll', { count: result.revokedCount }));
      setRevokeAllOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['devices', 'mine'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const removeRevoked = useMutation({
    mutationFn: () => api.post<{ removedCount: number }>('/devices/mine/remove-revoked', {}),
    onSuccess: (result) => {
      toast.success(t('devices.removedAllRevoked', { count: result.removedCount }));
      setRemoveRevokedOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['devices', 'mine'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const rows = devices.data?.data ?? [];
  const hasActive = rows.some((device) => device.status === 'active');
  const hasRevoked = rows.some((device) => device.status === 'revoked');
  const nameReady = deviceName.trim().length > 0;
  const renameReady = renameValue.trim().length > 0;

  return (
    <div>
      <PageHeader
        title={t('devices.title')}
        description={t('devices.description')}
        tipId="security.devices.trusted"
        headerActions={
          <div className="flex flex-wrap justify-end gap-2">
            {hasRevoked ? (
              <Button variant="outline" onClick={() => setRemoveRevokedOpen(true)}>
                {t('devices.removeAllRevoked')}
              </Button>
            ) : null}
            {hasActive ? (
              <Button variant="outline" onClick={() => setRevokeAllOpen(true)}>
                {t('devices.revokeAll')}
              </Button>
            ) : null}
            <Button
              onClick={() => {
                setDeviceName(suggestBrowserDeviceName());
                setEnrolOpen(true);
              }}
            >
              {t('devices.enrol')}
            </Button>
          </div>
        }
      />

      {rows.length === 0 ? (
        <p className="text-body-sm text-fg-secondary">{t('devices.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {rows.map((device) => (
            <li key={device.id}>
              <Card>
                <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium">{device.deviceName}</p>
                      <Badge variant={device.status === 'active' ? 'success' : 'neutral'}>
                        {t(`devices.status.${device.status}`)}
                      </Badge>
                    </div>
                    <p className="text-body-sm text-fg-secondary">
                      {t('devices.platform')}: {device.platform}
                    </p>
                    <p className="text-caption text-fg-secondary">
                      {t('devices.lastSeen')}:{' '}
                      {device.lastSeenAt ? formatDateTime(device.lastSeenAt) : '—'}
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    {device.status !== 'revoked' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setRenameTarget(device);
                          setRenameValue(device.deviceName);
                        }}
                      >
                        {t('devices.rename')}
                      </Button>
                    ) : null}
                    {device.status === 'active' ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setRevokeTarget(device)}
                      >
                        {t('devices.revoke')}
                      </Button>
                    ) : null}
                  </div>
                </CardContent>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <FormDialog
        open={enrolOpen}
        onOpenChange={setEnrolOpen}
        title={t('devices.enrolConfirm.title')}
        description={t('devices.enrolConfirm.description')}
        size="md"
        submitLabel={t('devices.enrolConfirm.confirm')}
        cancelLabel={tCommon('action.cancel')}
        closeLabel={tCommon('action.close')}
        submitting={enrol.isPending}
        submitDisabled={!nameReady}
        onSubmit={() => {
          if (!nameReady) return;
          enrol.mutate(deviceName);
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="device-enrol-name">{t('devices.enrolConfirm.name')}</Label>
          <Input
            id="device-enrol-name"
            value={deviceName}
            onChange={(event) => setDeviceName(event.target.value)}
            placeholder={t('devices.enrolConfirm.namePlaceholder')}
            maxLength={120}
            autoFocus
          />
        </div>
      </FormDialog>

      <FormDialog
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRenameTarget(null);
        }}
        title={t('devices.renameTitle')}
        size="md"
        submitLabel={t('devices.renameConfirm')}
        cancelLabel={tCommon('action.cancel')}
        closeLabel={tCommon('action.close')}
        submitting={rename.isPending}
        submitDisabled={!renameReady}
        onSubmit={() => {
          if (!renameTarget || !renameReady) return;
          rename.mutate({ id: renameTarget.id, name: renameValue });
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="device-rename-name">{t('devices.enrolConfirm.name')}</Label>
          <Input
            id="device-rename-name"
            value={renameValue}
            onChange={(event) => setRenameValue(event.target.value)}
            placeholder={t('devices.enrolConfirm.namePlaceholder')}
            maxLength={120}
            autoFocus
          />
        </div>
      </FormDialog>

      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(null);
        }}
        title={t('devices.revokeConfirm.title', {
          name: revokeTarget?.deviceName ?? '',
        })}
        description={tTips('security.devices.revoke')}
        confirmLabel={t('devices.revokeConfirm.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={revoke.isPending}
        onConfirm={() => {
          if (revokeTarget) revoke.mutate(revokeTarget.id);
        }}
      />

      <ConfirmDialog
        open={revokeAllOpen}
        onOpenChange={setRevokeAllOpen}
        title={t('devices.revokeAllConfirm.title')}
        description={tTips('security.devices.revokeAll')}
        confirmLabel={t('devices.revokeAllConfirm.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={revokeAll.isPending}
        onConfirm={() => revokeAll.mutate()}
      />

      <ConfirmDialog
        open={removeRevokedOpen}
        onOpenChange={setRemoveRevokedOpen}
        title={t('devices.removeAllRevokedConfirm.title')}
        description={tTips('security.devices.removeAllRevoked')}
        confirmLabel={t('devices.removeAllRevokedConfirm.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={removeRevoked.isPending}
        onConfirm={() => removeRevoked.mutate()}
      />
    </div>
  );
};
